import { useTranslation } from "@redotech/redo-i18n";
import { MetafieldMatchType } from "@redotech/redo-return-core-common/condition-options";
import type { ProductMetafieldState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo, useMemo } from "react";

export const ProductMetafieldDetails = memo(function ProductMetafieldDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<ProductMetafieldState>) {
  const { t } = useTranslation();
  const invalidRegex =
    state.comparison === MetafieldMatchType.REGEX && !isValidRegex(state.value);

  const comparisonOptions: RedoListItem<MetafieldMatchType>[] = useMemo(() => {
    const matchTypeLabels = {
      [MetafieldMatchType.EXACT]: t("Exact match"),
      [MetafieldMatchType.CONTAINS]: t("Contains"),
      [MetafieldMatchType.REGEX]: t("Regular expression"),
    };

    return Object.values(MetafieldMatchType).map((matchType) => ({
      id: matchType,
      value: matchType,
      type: "text" as const,
      text: matchTypeLabels[matchType],
    }));
  }, [t]);

  const selectedComparison = state.comparison
    ? comparisonOptions.find((option) => option.value === state.comparison)
    : undefined;

  return (
    <>
      <RedoTextInput
        label={t("Namespace")}
        setValue={(value) => setState({ ...state, namespace: value })}
        state={disabled ? "disabled" : "default"}
        value={state.namespace}
      />
      <RedoTextInput
        label={t("Key")}
        setValue={(value) => setState({ ...state, key: value })}
        state={disabled ? "disabled" : "default"}
        value={state.key}
      />
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        flexGrow
        label={t("Comparison")}
        options={comparisonOptions}
        optionSelected={({ item }) =>
          setState({ ...state, comparison: item.value })
        }
        placeholder={t("Select comparison...")}
        selectedItem={selectedComparison}
        size="xs"
      />
      <RedoTextInput
        errorText={invalidRegex ? t("Invalid regular expression") : undefined}
        label={t("Value")}
        setValue={(value) => setState({ ...state, value })}
        state={disabled ? "disabled" : "default"}
        value={state.value}
      />
    </>
  );
});

function isValidRegex(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}
